/**
 * Searchable design picker for product mapping (design style-code plan, Phase 2).
 *
 * Replaces the plain non-searchable Select: an operator can type a NAME fragment OR a style code
 * (FR / FW / LC / LT-001) to filter. The full ready-design list is already loaded on the Catalog page,
 * so this filters CLIENT-side (no new endpoint) via the tested `matchesDesignSearch` helper. The stored
 * value stays the opaque `designId`; the style code renders as a leading mono badge so the name stays clean.
 */
import { Check, ChevronsUpDown } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  type DesignSummary,
  designSizeHint,
  matchesDesignSearch,
  stripStyleCode,
} from './design-lib';

function StyleBadge({ code }: { code?: string }) {
  if (!code) return <span className="shrink-0 text-[10px] text-muted-foreground">—</span>;
  return (
    <Badge
      variant="secondary"
      className="shrink-0 font-mono text-[10px] font-semibold tracking-wide"
    >
      {code}
    </Badge>
  );
}

export function DesignCombobox({
  value,
  designs,
  ariaLabel,
  onChange,
}: {
  value: string;
  designs: DesignSummary[];
  ariaLabel: string;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selected = designs.find((d) => d.designId === value);
  const matches = designs.filter((d) => matchesDesignSearch(d, query));

  function pick(designId: string) {
    onChange(designId);
    setOpen(false);
  }

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          disabled={designs.length === 0}
          className="w-full min-w-0 justify-between font-normal"
        >
          {selected ? (
            <span className="flex min-w-0 items-center gap-2">
              <StyleBadge code={selected.styleCode} />
              <span className="truncate">{stripStyleCode(selected.name, selected.styleCode)}</span>
            </span>
          ) : (
            <span className="truncate text-muted-foreground">
              {designs.length === 0 ? 'No ready designs' : 'Pick a design'}
            </span>
          )}
          <ChevronsUpDown className="ml-2 size-4 shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        {/* Client-side filter (matchesDesignSearch) — cmdk's own filter is off so name OR code both hit. */}
        <Command shouldFilter={false}>
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder="Search by name or style code…"
            autoFocus
          />
          <CommandList className="max-h-[320px]">
            {matches.length === 0 ? (
              <CommandEmpty>No designs match “{query}”.</CommandEmpty>
            ) : (
              <CommandGroup>
                {matches.map((d) => (
                  <CommandItem
                    key={d.designId}
                    value={d.designId}
                    onSelect={() => pick(d.designId)}
                  >
                    <div className="flex flex-1 items-center gap-2">
                      <StyleBadge code={d.styleCode} />
                      <span className="truncate">{stripStyleCode(d.name, d.styleCode)}</span>
                      <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                        {designSizeHint(d)}
                      </span>
                    </div>
                    {d.designId === value && (
                      <Check className="ml-2 size-4 shrink-0 text-primary" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
